import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./GifteeAppBottomNav.module.css";

const ITEMS = [
  { id: "home", path: "/home", label: "Home", icon: "fa-house" },
  { id: "giftees", path: "/all-giftees", label: "Giftees", icon: "fa-users" },
  { id: "occasions", path: "/all-occasions", label: "Occasions", icon: "fa-calendar" },
  { id: "settings", path: "/settings", label: "Settings", icon: "fa-gear" },
];

/**
 * Fixed bottom nav — Figma `NavMenu` (525:16496); docked on sage screens.
 *
 * Viewport/keyboard math (Safari bottom-chrome padding via
 * --gg-bottom-nav-pad-bottom-effective) is owned by the app-level
 * useViewportInsets hook. This component only publishes its own measured height
 * to --gg-bottom-nav-reserve so screen shells can reserve clearance.
 */
export default function GifteeAppBottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const navEl = navRef.current;
    const root = document.documentElement;
    if (!navEl) return undefined;

    const measure = () => {
      const styles = getComputedStyle(root);
      const staticMax = parseFloat(
        styles.getPropertyValue("--gg-bottom-nav-reserve-static"),
      );
      const reserve = Math.max(
        navEl.offsetHeight,
        Number.isFinite(staticMax) ? staticMax : 0,
      );
      root.style.setProperty("--gg-bottom-nav-reserve", `${reserve}px`);
    };

    measure();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(navEl);
    }
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
      root.style.removeProperty("--gg-bottom-nav-reserve");
    };
  }, []);

  return (
    <nav ref={navRef} className={styles.bar} aria-label="Main">
      <div className={styles.inner}>
        {ITEMS.map((item) => {
          const active =
            item.path != null &&
            (item.path === "/home"
              ? pathname === "/home" || pathname === "/start"
              : pathname === item.path);
          return (
            <button
              key={item.id}
              type="button"
              className={active ? styles.itemActive : styles.item}
              aria-current={active ? "page" : undefined}
              disabled={item.path == null}
              title={item.path == null ? "Coming soon" : item.label}
              onClick={() => {
                if (item.path) navigate(item.path);
              }}
            >
              <span className={styles.iconWrap} aria-hidden="true">
                <i className={`fa-solid ${item.icon}`} />
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
